/**
 * Mobile Navigation Configuration
 *
 * Derives the phone navigation from the canonical `getMenuItems` list so the
 * sidebar (desktop) and the bottom bar (mobile) can never drift apart.
 *
 * Android/Material guidance is 3–5 bottom destinations, so each role gets its
 * four highest-traffic screens plus a "More" sheet holding the rest. Anything
 * in `getMenuItems` that is not promoted to `primary` automatically falls into
 * `more`, which means a new sidebar entry shows up on mobile without any extra
 * wiring here.
 */

import { IconResources, IconTrophy, IconSettings } from '../components/icons';

import { getMenuItems } from './MenuItems';

/**
 * Paths promoted to the bottom bar, in display order, per role.
 * Keep these to four – the fifth slot is always "More".
 *
 * `shortKey` overrides the sidebar label: a tab is ~75px wide, and the full
 * labels overflow it ("Boshqaruv paneli" renders as "Boshqaruv pa…", and the
 * Russian "Панель управления" is worse). The sidebar keeps the long form.
 */
const PRIMARY_ITEMS = {
  ADMIN: [
    { path: '/admin/dashboard', shortKey: 'navigation.short.dashboard' },
    { path: '/admin/groups', shortKey: 'navigation.short.groups' },
    { path: '/admin/users', shortKey: 'navigation.short.users' },
    { path: '/admin/messages', shortKey: 'navigation.short.messages' },
  ],
  TEACHER: [
    { path: '/teacher/dashboard', shortKey: 'navigation.short.dashboard' },
    { path: '/teacher/groups', shortKey: 'navigation.short.groups' },
    { path: '/teacher/lessons', shortKey: 'navigation.short.schedule' },
    { path: '/teacher/messages', shortKey: 'navigation.short.messages' },
  ],
  STUDENT: [
    { path: '/student/dashboard', shortKey: 'navigation.short.dashboard' },
    { path: '/student/subjects', shortKey: 'navigation.short.courses' },
    { path: '/student/lessons', shortKey: 'navigation.short.schedule' },
    { path: '/student/messages', shortKey: 'navigation.short.messages' },
  ],
};

/**
 * Flatten a menu entry that may carry `children` (e.g. the teacher question
 * bank submenu) into a list of navigable leaves.
 */
const flatten = (item) => {
  if (!item.children?.length) return [item];
  return item.children.map((child) => ({
    ...child,
    icon: child.icon ?? item.icon,
  }));
};

/**
 * Cross-role entry points that live in the desktop header. On a phone the
 * header has no room for them, so they move into the More sheet.
 */
const getExtraMoreItems = (t) => [
  {
    key: 'more-resources',
    icon: <IconResources />,
    label: t('navigation.resources'),
    path: '/resources',
  },
  {
    key: 'more-ranking',
    icon: <IconTrophy />,
    label: t('navigation.ranking'),
    path: '/ranking',
  },
  {
    key: 'more-settings',
    icon: <IconSettings />,
    label: t('menu.settings'),
    path: '/settings',
  },
];

/**
 * Build the mobile navigation model for a role.
 *
 * @param {string} role - ADMIN | TEACHER | STUDENT
 * @param {Function} t - translation function
 * @returns {{ primary: Array, more: Array }}
 */
export const getMobileNav = (role, t) => {
  const all = getMenuItems(role, t).flatMap(flatten);
  const primaryItems = PRIMARY_ITEMS[role] ?? [];
  const primaryPaths = primaryItems.map((item) => item.path);

  // Preserve the configured order of PRIMARY_ITEMS rather than menu order.
  const primary = primaryItems
    .map(({ path, shortKey }) => {
      const item = all.find((candidate) => candidate.path === path);
      return item ? { ...item, label: t(shortKey) } : null;
    })
    .filter(Boolean);

  const more = [
    ...all.filter((item) => !primaryPaths.includes(item.path)),
    ...getExtraMoreItems(t),
  ];

  return { primary, more };
};

/**
 * Resolve which navigation path is currently active.
 * Falls back to longest-prefix matching so nested routes (e.g.
 * `/student/subjects/12/tasks`) still light up their parent tab.
 *
 * @param {Array} items - candidate items with a `path`
 * @param {string} pathname - current location pathname
 * @returns {string|null}
 */
export const resolveActivePath = (items, pathname) => {
  const exact = items.find((item) => item.path === pathname);
  if (exact) return exact.path;

  const prefixMatches = items
    .filter((item) => item.path !== '/' && pathname.startsWith(`${item.path}/`))
    .sort((a, b) => b.path.length - a.path.length);

  return prefixMatches[0]?.path ?? null;
};

export default getMobileNav;
